import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Smartphone, MessageSquare, CheckCircle2, AlertCircle, X, ArrowRight, Loader2, CreditCard, ShieldCheck } from 'lucide-react';
import { sendOtp, verifyOtp, getCreditBalance, createCreditCheckout } from '../../lib/api';
import { useUser } from '../../contexts/UserContext';
interface PhoneLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (phoneNumber: string, sessionToken: string) => void;
  creditsNeeded: number;
  estimatedTotal: number;
}
type Step = 'phone' | 'otp' | 'confirm' | 'insufficient';
export function PhoneLinkModal({
  isOpen,
  onClose,
  onConfirm,
  creditsNeeded,
  estimatedTotal
}: PhoneLinkModalProps) {
  const {
    user,
    refreshUser
  } = useUser();
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creditsAvailable, setCreditsAvailable] = useState<number | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      if (user?.linkedPhone) {
        // If phone is already linked, check credits and go to confirm/insufficient
        checkCredits();
      } else {
        setStep('phone');
        setPhone('');
        setOtp('');
        setError(null);
      }
    }
  }, [isOpen, user?.linkedPhone]);
  const checkCredits = async () => {
    setLoading(true);
    try {
      const balance = await getCreditBalance();
      setCreditsAvailable(balance);
      if (balance >= creditsNeeded) {
        setStep('confirm');
      } else {
        setStep('insufficient');
      }
    } catch (err) {
      console.error('Error checking credits:', err);
      setError('Failed to check credit balance');
    } finally {
      setLoading(false);
    }
  };
  const handleSendOtp = async () => {
    if (!phone || phone.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Format phone number if needed (ensure +1)
      const formattedPhone = phone.startsWith('+') ? phone : `+1${phone.replace(/\D/g, '')}`;
      await sendOtp(formattedPhone);
      setStep('otp');
    } catch (err) {
      console.error('Error sending OTP:', err);
      setError('Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const formattedPhone = phone.startsWith('+') ? phone : `+1${phone.replace(/\D/g, '')}`;
      const result = await verifyOtp(formattedPhone, otp);
      if (result.success) {
        // Fail loud if token is missing — something broke upstream
        if (!result.session_token) {
          setError('Verification succeeded but no session token was returned. Please try again.');
          return;
        }
        setSessionToken(result.session_token);
        await refreshUser();
        setCreditsAvailable(result.credits_remaining);
        if (result.credits_remaining >= creditsNeeded) {
          setStep('confirm');
        } else {
          setStep('insufficient');
        }
      } else {
        setError('Invalid verification code');
      }
    } catch (err) {
      console.error('Error verifying OTP:', err);
      setError('Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1246">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-1247" />

      {/* Modal */}
      <div className="relative bg-card border border-border rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200" data-id="element-1248">
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors z-10" data-id="element-1249">
          <X size={20} data-id="element-1250" />
        </button>

        {/* Progress Bar (optional visual flair) */}
        <div className="h-1 bg-secondary w-full" data-id="element-1251">
          <div className="h-full bg-primary transition-all duration-500 ease-out" style={{
          width: step === 'phone' ? '33%' : step === 'otp' ? '66%' : '100%'
        }} data-id="element-1252" />
        </div>

        <div className="p-6" data-id="element-1253">
          {/* STEP 1: PHONE ENTRY */}
          {step === 'phone' && <div className="space-y-6" data-id="element-1254">
              <div className="text-center space-y-2" data-id="element-1255">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto" data-id="element-1256">
                  <Smartphone size={24} className="text-primary" data-id="element-1257" />
                </div>
                <h2 className="text-xl font-bold" data-id="element-1258">Connect Your Account</h2>
                <p className="text-sm text-muted-foreground" data-id="element-1259">
                  Link your phone number to access your credits and verify your
                  identity.
                </p>
              </div>

              <div className="space-y-4" data-id="element-1260">
                <div className="space-y-2" data-id="element-1261">
                  <label className="text-sm font-medium" data-id="element-1262">Phone Number</label>
                  <div className="relative" data-id="element-1263">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium" data-id="element-1264">
                      🇺🇸 +1
                    </span>
                    <Input placeholder="(555) 000-0000" className="pl-14" value={phone} onChange={e => setPhone(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSendOtp()} autoFocus data-id="element-1265" />
                  </div>
                </div>

                {error && <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-md flex items-center gap-2" data-id="element-1266">
                    <AlertCircle size={16} data-id="element-1267" />
                    {error}
                  </div>}

                <Button className="w-full h-11" onClick={handleSendOtp} disabled={loading || !phone} data-id="element-1268">
                  {loading ? <>
                      <Loader2 size={16} className="mr-2 animate-spin" data-id="element-1269" />
                      Sending...
                    </> : <>
                      Send Verification Code
                      <ArrowRight size={16} className="ml-2" data-id="element-1270" />
                    </>}
                </Button>

                <p className="text-xs text-center text-muted-foreground" data-id="element-1271">
                  We'll send a 6-digit code via SMS. Standard message and data
                  rates may apply.
                </p>
              </div>
            </div>}

          {/* STEP 2: OTP ENTRY */}
          {step === 'otp' && <div className="space-y-6" data-id="element-1272">
              <div className="text-center space-y-2" data-id="element-1273">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto" data-id="element-1274">
                  <MessageSquare size={24} className="text-primary" data-id="element-1275" />
                </div>
                <h2 className="text-xl font-bold" data-id="element-1276">Check Your Phone</h2>
                <p className="text-sm text-muted-foreground" data-id="element-1277">
                  Enter the 6-digit code sent to{' '}
                  <span className="font-medium text-foreground" data-id="element-1278">{phone}</span>
                </p>
              </div>

              <div className="space-y-4" data-id="element-1279">
                <div className="flex justify-center" data-id="element-1280">
                  <Input className="text-center text-2xl tracking-[0.5em] font-mono h-14 w-48" placeholder="000000" maxLength={6} value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()} autoFocus data-id="element-1281" />
                </div>

                {error && <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-md flex items-center gap-2" data-id="element-1282">
                    <AlertCircle size={16} data-id="element-1283" />
                    {error}
                  </div>}

                <Button className="w-full h-11" onClick={handleVerifyOtp} disabled={loading || otp.length !== 6} data-id="element-1284">
                  {loading ? <>
                      <Loader2 size={16} className="mr-2 animate-spin" data-id="element-1285" />
                      Verifying...
                    </> : 'Verify Code'}
                </Button>

                <div className="text-center" data-id="element-1286">
                  <button onClick={() => setStep('phone')} className="text-sm text-muted-foreground hover:text-primary transition-colors" data-id="element-1287">
                    Wrong number? Go back
                  </button>
                </div>
              </div>
            </div>}

          {/* STEP 3: CONFIRM (ENOUGH CREDITS) */}
          {step === 'confirm' && <div className="space-y-6" data-id="element-1288">
              <div className="text-center space-y-2" data-id="element-1289">
                <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mx-auto" data-id="element-1290">
                  <CheckCircle2 size={24} className="text-success" data-id="element-1291" />
                </div>
                <h2 className="text-xl font-bold" data-id="element-1292">Ready to Fetch</h2>
                <p className="text-sm text-muted-foreground" data-id="element-1293">
                  Your account is verified and you have enough credits.
                </p>
              </div>

              <div className="bg-secondary/30 rounded-lg p-4 space-y-3" data-id="element-1294">
                <div className="flex justify-between items-center" data-id="element-1295">
                  <span className="text-sm text-muted-foreground" data-id="element-1296">
                    Total Comments
                  </span>
                  <span className="font-semibold" data-id="element-1297">
                    {estimatedTotal.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1298">
                  <span className="text-sm text-muted-foreground" data-id="element-1299">
                    Credits Required
                  </span>
                  <span className="font-semibold text-primary" data-id="element-1300">
                    {creditsNeeded}
                  </span>
                </div>
                <div className="h-px bg-border/50 my-2" data-id="element-1301" />
                <div className="flex justify-between items-center" data-id="element-1302">
                  <span className="text-sm font-medium" data-id="element-1303">Your Balance</span>
                  <span className="font-bold text-success" data-id="element-1304">
                    {creditsAvailable} credits
                  </span>
                </div>
              </div>

              <div className="p-3 bg-info/10 border border-info/20 rounded-lg flex gap-3 items-start" data-id="element-1305">
                <ShieldCheck size={16} className="text-info mt-0.5 flex-shrink-0" data-id="element-1306" />
                <p className="text-xs text-info leading-relaxed" data-id="element-1307">
                  Credits are only deducted for successfully fetched comments.
                  If the fetch fails, you'll be refunded automatically.
                </p>
              </div>

              <Button className="w-full h-11 text-base" onClick={() => {
            if (!sessionToken) {
              setError('Session token missing. Please re-verify your phone number.');
              setStep('phone');
              return;
            }
            const formattedPhone = phone.startsWith('+') ? phone : `+1${phone.replace(/\D/g, '')}`;
            onConfirm(formattedPhone, sessionToken);
          }} data-id="element-1308">
                Confirm Fetch ({creditsNeeded} credits)
              </Button>
            </div>}

          {/* STEP 4: INSUFFICIENT CREDITS */}
          {step === 'insufficient' && <div className="space-y-6" data-id="element-1309">
              <div className="text-center space-y-2" data-id="element-1310">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto" data-id="element-1311">
                  <AlertCircle size={24} className="text-destructive" data-id="element-1312" />
                </div>
                <h2 className="text-xl font-bold text-destructive" data-id="element-1313">
                  Insufficient Credits
                </h2>
                <p className="text-sm text-muted-foreground" data-id="element-1314">
                  You need more credits to perform this analysis.
                </p>
              </div>

              <div className="bg-secondary/30 rounded-lg p-4 space-y-3" data-id="element-1315">
                <div className="flex justify-between items-center" data-id="element-1316">
                  <span className="text-sm text-muted-foreground" data-id="element-1317">
                    Credits Required
                  </span>
                  <span className="font-semibold" data-id="element-1318">{creditsNeeded}</span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1319">
                  <span className="text-sm text-muted-foreground" data-id="element-1320">
                    Your Balance
                  </span>
                  <span className="font-semibold" data-id="element-1321">{creditsAvailable}</span>
                </div>
                <div className="h-px bg-border/50 my-2" data-id="element-1322" />
                <div className="flex justify-between items-center text-destructive" data-id="element-1323">
                  <span className="text-sm font-bold" data-id="element-1324">Shortfall</span>
                  <span className="font-bold" data-id="element-1325">
                    {Math.max(0, creditsNeeded - (creditsAvailable || 0))}{' '}
                    credits needed
                  </span>
                </div>
              </div>

              <Button className="w-full h-11 text-base" disabled={loading} onClick={async () => {
            setLoading(true);
            setError(null);
            try {
              const {
                checkout_url
              } = await createCreditCheckout(creditsNeeded);
              window.location.href = checkout_url;
            } catch (err) {
              setError('Failed to start checkout. Please try again.');
            } finally {
              setLoading(false);
            }
          }} data-id="element-1326">
                {loading ? <Loader2 size={16} className="mr-2 animate-spin" data-id="element-1327" /> : <CreditCard size={18} className="mr-2" data-id="element-1328" />}
                Buy Credits
              </Button>

              <button onClick={onClose} className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors" data-id="element-1329">
                Maybe later
              </button>
            </div>}
        </div>
      </div>
    </div>;
}